import React, {FC, useState} from 'react';

import Box from '@mui/material/Box';
import styled from '@mui/system/styled';

import ButtonPanel from '@/components/ButtonPanel';
import AboutPanel from '@/components/Panels/AboutPanel';
import SettingsPanel from '@/components/Panels/SettingsPanel';
import ParcelPanel from '@/components/Panels/Parcel';
import ProfilePanel from '@/components/Panels/ProfilePanel';
import ZoneDataPanel from '@/components/Panels/ZoneDataPanel';

import {ParcelSummary} from '@/domain/entities/ParcelSummary';
import {User} from '@/domain/entities/User';
import {Dispute} from '@/domain/entities/Dispute';
import {Stats} from '@/domain/entities/Stats';
import {DocumentProperties, ParcelDocumentCreation} from '@/domain/entities/ParcelDocument';
import {Project} from '@/domain/entities/Project';
import Logo from '@/components/logos/Logo';
import {SxProps} from '@mui/system';
import Paper from '@mui/material/Paper';

const Main = styled(Box)({
  flexGrow: 1,
  padding: 0,
  position: 'absolute',
  top: 0,
  bottom: 0,
  right: 0,
  left: 0
});

export enum Mode {
  PARCEL = 'parcel',
  SETTINGS = 'settings',
  PROFILE = 'profile',
  ZONE_DATA = 'zoneData'
}

export type LayoutProps = {
  children: React.ReactElement,
  selectedParcelUuid?: string,
  parcelSummary?: ParcelSummary,
  mapStyle: string,
  selectedOverlays: Array<string>,
  user?: User,
  stats?: Stats,
  mode?: Mode,
  dispute?: Dispute,
  documents?: Array<DocumentProperties>,
  project?: Project,
  onModeChanged: (mode?: Mode) => void,
  onMapStyleChanged: (mapStyle: string) => void
  onOverlaysChanged: (overlays: Array<string>) => void
  onProjectListClicked: () => void
  onLogout: () => void,
  onDisputeSaved: (dispute: Omit<Dispute, 'geom'>) => void,
  toggleGeometryVisibility: (isGeometryVisible: boolean) => void,
  onDocumentUpload: (dataForUpload: ParcelDocumentCreation) => void,
  onDocumentDownload: (dataUrl: string, name: string) => void,
  onDocumentDelete: (uuid: string) => void
};

const Layout:  FC<LayoutProps> = ({
  children,
  selectedParcelUuid, 
  parcelSummary, 
  mapStyle,
  selectedOverlays,
  user,
  stats,
  mode,
  dispute,
  documents,
  project,
  onModeChanged,
  onLogout,
  onMapStyleChanged,
  onOverlaysChanged,
  onProjectListClicked,
  onDisputeSaved,
  toggleGeometryVisibility,
  onDocumentUpload,
  onDocumentDownload,
  onDocumentDelete
}) => {

  const [isAboutOpen, setAboutOpen] = useState(false);

  const handleAboutClicked = () => setAboutOpen(!isAboutOpen);

  return <>
    <Paper sx={styles.logoPosition} elevation={0} id="Logo Principado">
      <Logo/>
    </Paper>
    <Main>
      {children}
    </Main>
    <ButtonPanel
      mode={mode}
      stats={stats}
      onModeChanged={onModeChanged}
      selectedParcelUuid={selectedParcelUuid}
      onAboutClicked={handleAboutClicked}
      onProjectListClicked={onProjectListClicked}
    />
    {mode === Mode.PARCEL &&
      <ParcelPanel
        project={project}
        user={user}
        parcelSummary={parcelSummary}
        dispute={dispute}
        documents={documents}
        onDisputeSaved={onDisputeSaved} 
        toggleGeometryVisibility={toggleGeometryVisibility}
        onDocumentUpload={onDocumentUpload}
        onDocumentDownload={onDocumentDownload}
        onDocumentDelete={onDocumentDelete}
      />
    }
    {mode === Mode.SETTINGS &&
      <SettingsPanel
        project={project}
        user={user}
        mapStyle={mapStyle}
        onMapStyleChanged={onMapStyleChanged}
        selectedOverlays={selectedOverlays}
        onOverlaysChanged={onOverlaysChanged}
      />}
    {mode === Mode.ZONE_DATA &&
      <ZoneDataPanel stats={stats}/>}
    {mode === Mode.PROFILE &&
      <ProfilePanel
        user={user}
        onLogout={onLogout}
      />}
    {isAboutOpen &&
      <AboutPanel isOpen={isAboutOpen} onClose={handleAboutClicked}/>}
  </>;
};

export default Layout;

const styles: Record<string, SxProps> = {
  logoPosition: {
    position: 'fixed',
    bgcolor: 'transparent',
    /*width: CARDS_WIDTH,*/
    zIndex: 12500,
    p: 1
  }
};
